import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
   bookPracticalExamSlotForStudent,
   deletePracticalExamReservation,
   downloadPracticalExamIndividualDocument,
   updatePracticalExamReservation,
} from "../../api/practicalExamSlotsService";
import { EXAM_DOCUMENTS_ENABLED } from "../../constants/examDocuments";
import apiClientService from "../../api/ApiClientService";
import {
   normalizePracticalExamColorToken,
   PRACTICAL_EXAM_COLOR_GRID_COLUMNS,
   PRACTICAL_EXAM_COLOR_HINTS,
   PRACTICAL_EXAM_COLOR_LABELS,
   PRACTICAL_EXAM_COLOR_TOKENS,
   calendarEventColorSwatchStyle,
   practicalExamColorTokenSuffix,
   resolvePracticalExamReservationApiId,
   resolvePracticalExamReservationColorForSave,
} from "../../constants/practicalExamCalendar";
import { fetchStudents } from "../../store/studentsSlice";
import ConfirmDeleteButton from "../Common/ConfirmDeleteButton";
import UIIcon from "../Common/UIIcon";
import { copyTextToClipboardSafe } from "../APanel/CalendarPlus/utils";

const STATUS_OPTIONS = ["PASSED", "FAILED", "ABSENT", "CANCELLED"];
const SEARCH_RESULTS_LIMIT = 10;
const EMPTY_USERS = [];
const STATUS_LABELS = {
   SCHEDULED: "Programat",
   PASSED: "Promovat",
   FAILED: "Respins",
   ABSENT: "Absent",
   CANCELLED: "Anulat",
};

function readFirst(...values) {
   for (const value of values) {
      if (value !== "" && value != null) return value;
   }
   return "";
}

function readReservationApiId(value) {
   return resolvePracticalExamReservationApiId(value);
}

function colorSwatchStyle(token) {
   return calendarEventColorSwatchStyle(token);
}

function hasUserRole(user) {
   const role = String(
      user?.role ?? user?.Role ?? user?.userRole ?? "",
   ).toUpperCase();
   if (role === "USER") return true;
   const roles = Array.isArray(user?.roles)
      ? user.roles.map((entry) => String(entry).toUpperCase())
      : [];
   return roles.includes("USER");
}

const onlyDigits = (value = "") => {
   const raw = value == null ? "" : String(value);
   const normalized =
      typeof raw.normalize === "function" ? raw.normalize("NFKC") : raw;
   return normalized.replace(/\D/g, "");
};

function normalizePhoneForStudentCreate(phoneValue) {
   const digits = onlyDigits(phoneValue);
   if (!digits) return "";
   const noLeadingZeros = digits.replace(/^0+/, "");
   return noLeadingZeros || digits;
}

function splitFullName(full = "") {
   const parts = String(full || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
   if (!parts.length) return { firstName: "", lastName: "" };
   if (parts.length === 1) return { firstName: parts[0], lastName: "" };
   return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function escapeRegExp(value) {
   return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightText(text, query) {
   if (!text) return "";
   if (!query) return text;

   const safe = escapeRegExp(query);
   if (!safe) return text;

   const parts = String(text).split(new RegExp(`(${safe})`, "gi"));
   const qLower = String(query || "").toLowerCase();
   return parts.map((part, index) =>
      part.toLowerCase() === qLower ? (
         <i key={`${part}-${index}`} className="highlight">
            {part}
         </i>
      ) : (
         part
      ),
   );
}

function pickStudentPhoneValue(user) {
   return String(
      user?.phone ?? user?.phoneNumber ?? user?.mobile ?? user?.telefon ?? "",
   ).trim();
}

function pickPersonPhoneValue(person) {
   if (!person || typeof person !== "object") return "";
   return String(
      readFirst(
         person.phone,
         person.phoneNumber,
         person.mobile,
         person.telefon,
         person.tel,
      ),
   ).trim();
}

function buildPhoneLookupKeys(value) {
   const raw = onlyDigits(value);
   if (!raw) return [];

   const keys = [];
   const push = (candidate) => {
      const digits = onlyDigits(candidate);
      if (!digits || keys.includes(digits)) return;
      keys.push(digits);
   };

   const addCountryVariants = (digits) => {
      const normalized = onlyDigits(digits);
      if (!normalized.startsWith("373")) return;
      const local = normalized.slice(3);
      push(local);
      if (local.length === 9 && local.startsWith("0")) {
         push(local.slice(1));
      }
   };

   push(raw);

   const withoutIntlPrefix = raw.replace(/^00/, "");
   if (withoutIntlPrefix && withoutIntlPrefix !== raw) {
      push(withoutIntlPrefix);
   }

   addCountryVariants(raw);
   addCountryVariants(withoutIntlPrefix);

   if (raw.startsWith("0")) push(raw.replace(/^0+/, ""));
   if (withoutIntlPrefix && withoutIntlPrefix.startsWith("0")) {
      push(withoutIntlPrefix.replace(/^0+/, ""));
   }

   return keys;
}

function findStudentByPhoneDigits(exactPhoneDigits, value) {
   const map = exactPhoneDigits instanceof Map ? exactPhoneDigits : null;
   if (!map) return null;

   const lookupKeys = buildPhoneLookupKeys(value);
   for (const key of lookupKeys) {
      const hit = map.get(key);
      if (hit) return hit;
   }
   return null;
}

function matchesPhoneDigits(entry, value) {
   const typedDigits = onlyDigits(value);
   if (!typedDigits) return false;

   const entryKeys = Array.isArray(entry?.phoneLookupKeys)
      ? entry.phoneLookupKeys.filter(Boolean)
      : [];
   if (!entryKeys.length) return false;

   const typedKeys = buildPhoneLookupKeys(value);
   if (typedKeys.some((key) => entryKeys.includes(key))) return true;

   if (typedDigits.length < 4) return false;
   return entryKeys.some((key) => key.includes(typedDigits));
}

function normalizeNameForSearch(value) {
   return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
}

function matchesNameQuery(entry, normalizedQuery) {
   const nameKey = entry?.nameKey || "";
   if (!nameKey || !normalizedQuery) return false;

   const tokens = normalizedQuery.split(" ").filter(Boolean);
   if (!tokens.length) return false;

   return tokens.every((token) => nameKey.includes(token));
}

function buildStudentLookup(list) {
   const source = Array.isArray(list) ? list : EMPTY_USERS;
   const exactPhoneDigits = new Map();
   const searchIndex = [];

   for (const user of source) {
      if (!hasUserRole(user)) continue;
      const phone = pickStudentPhoneValue(user);
      const phoneLookupKeys = buildPhoneLookupKeys(phone);

      for (const key of phoneLookupKeys) {
         if (!exactPhoneDigits.has(key)) exactPhoneDigits.set(key, user);
      }

      searchIndex.push({
         user,
         phoneLookupKeys,
         nameKey: normalizeNameForSearch(personName(user)),
      });
   }

   return { searchIndex, exactPhoneDigits };
}

function findStudentsForSearch(list, query = {}, limit = 20) {
   const source = Array.isArray(list) ? list : EMPTY_USERS;
   const { nameQuery = "", phoneQuery = "" } = query || {};
   const typedDigits = onlyDigits(phoneQuery);
   const typedName = normalizeNameForSearch(nameQuery);
   if (!typedDigits && !typedName) return [];

   const out = [];
   const seenIds = new Set();
   for (const entry of source) {
      const user = entry?.user || entry;
      if (!hasUserRole(user)) continue;

      const phoneOk = typedDigits ? matchesPhoneDigits(entry, typedDigits) : false;
      const nameOk = typedName ? matchesNameQuery(entry, typedName) : false;
      if (!phoneOk && !nameOk) continue;

      const id = String(user?.id ?? "");
      if (id) {
         if (seenIds.has(id)) continue;
         seenIds.add(id);
      }

      out.push(user);
      if (out.length >= limit) break;
   }
   return out;
}

function pad2(value) {
   return String(value).padStart(2, "0");
}

function normalizeColorToken(value, fallback = "--black-t") {
   return normalizePracticalExamColorToken(value, fallback);
}

function normalizeReservationColor(reservation) {
   return normalizeColorToken(
      readFirst(
         reservation?.color,
         reservation?.colorToken,
         reservation?.calendarColor,
         reservation?.eventColor,
         reservation?.uiColor,
         reservation?.raw?.color,
         reservation?.raw?.colorToken,
         reservation?.raw?.calendarColor,
         reservation?.raw?.eventColor,
         reservation?.raw?.reservation?.color,
         reservation?.raw?.reservation?.colorToken,
         reservation?.raw?.reservation?.calendarColor,
         reservation?.raw?.reservation?.eventColor,
         "--black-t",
      ),
   );
}

function readReservationNote(reservation) {
   return String(
      readFirst(
         reservation?.privateMessage,
         reservation?.note,
         reservation?.raw?.privateMessage,
         reservation?.raw?.note,
         reservation?.raw?.reservation?.privateMessage,
         reservation?.raw?.reservation?.note,
         "",
      ),
   );
}

function colorTokenSuffix(value) {
   return practicalExamColorTokenSuffix(value);
}

function personName(value) {
   if (!value || typeof value !== "object") return "";

   const fullName = readFirst(
      value.fullName,
      value.name,
      value.displayName,
      value.username,
      value.userName,
   );
   if (fullName) return String(fullName).trim();

   return [value.firstName, value.lastName].filter(Boolean).join(" ").trim();
}

function studentName(student) {
   return String(personName(student) || `Student #${student?.id ?? ""}`).trim();
}

function reservationStudentName(reservation) {
   return (
      reservation?.name ||
      personName(reservation?.user) ||
      personName(reservation?.student) ||
      personName(reservation?.raw?.user) ||
      personName(reservation?.raw?.student) ||
      studentName(reservation)
   );
}

function normalizeStatus(status) {
   return String(status || "SCHEDULED")
      .trim()
      .toUpperCase();
}

function normalizeEditableStatus(status) {
   const normalized = normalizeStatus(status);
   return STATUS_OPTIONS.includes(normalized) ? normalized : "";
}

function formatDateLabel(value) {
   const raw = String(value || "").trim();
   if (!raw) return "—";

   const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
   if (match) return `${match[3]}.${match[2]}.${match[1]}`;

   const parsed = new Date(raw);
   if (!Number.isNaN(parsed.getTime())) {
      return `${pad2(parsed.getDate())}.${pad2(parsed.getMonth() + 1)}.${parsed.getFullYear()}`;
   }

   return raw;
}

function normalizeTimeLabel(value) {
   const raw = String(value || "").trim();
   const match = raw.match(/(\d{1,2}):(\d{2})/);
   if (!match) return raw || "—";
   return `${pad2(Number(match[1]))}:${match[2]}`;
}

function addMinutesToTime(time, minutesToAdd) {
   const match = String(time || "").match(/^(\d{1,2}):(\d{2})$/);
   if (!match) return normalizeTimeLabel(time);

   const hours = Number(match[1]);
   const minutes = Number(match[2]);
   if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return normalizeTimeLabel(time);
   }

   const totalMinutes = hours * 60 + minutes + Number(minutesToAdd || 0);
   const dayMinutes = ((totalMinutes % 1440) + 1440) % 1440;
   return `${pad2(Math.floor(dayMinutes / 60))}:${pad2(dayMinutes % 60)}`;
}

function timeForSeat(time, seat) {
   return normalizeTimeLabel(time);
}

function gearboxLabel(value) {
   const normalized = String(value || "")
      .trim()
      .toLowerCase();
   if (normalized.includes("auto")) return "Automat";
   if (normalized.includes("manual") || normalized.includes("mecanic")) {
      return "Manual";
   }
   return String(value || "—").trim() || "—";
}

function formatReservationDateTime(date, time) {
   const parts = [date, time].filter(
      (value) => value && String(value).trim() && value !== "—",
   );
   return parts.length ? parts.join(" · ") : "—";
}

function buildReservationCardData(slot, reservation, seatNumber) {
   const raw = reservation?.raw || {};
   const rawSlot = readFirst(raw.slot, raw.practicalExamSlot, raw.examSlot, {});
   const dateValue = readFirst(
      reservation?.dateKey,
      reservation?.date,
      raw.dateKey,
      raw.date,
      raw.examDate,
      raw.slotDate,
      rawSlot?.dateKey,
      rawSlot?.date,
      rawSlot?.examDate,
      slot?.dateKey,
      slot?.date,
      slot?.raw?.date,
      slot?.raw?.examDate,
   );
   const baseTime = readFirst(
      slot?.timeKey,
      slot?.time,
      slot?.raw?.time,
      slot?.raw?.slotTime,
      rawSlot?.timeKey,
      rawSlot?.time,
      rawSlot?.slotTime,
      reservation?.timeKey,
      reservation?.time,
      raw.timeKey,
      raw.time,
      raw.slotTime,
   );
   const seat = readFirst(
      seatNumber,
      reservation?.position,
      reservation?.positionInCar,
      raw.position,
      raw.positionInCar,
      "",
   );
   const instructor = readFirst(
      reservation?.instructorName,
      personName(raw.instructor),
      personName(raw.teacher),
      personName(rawSlot?.instructor),
      personName(rawSlot?.teacher),
      slot?.instructorName,
      personName(slot?.raw?.instructor),
      personName(slot?.raw?.teacher),
      slot?.instructorId ? `Instructor #${slot.instructorId}` : "",
   );

   return {
      date: formatDateLabel(dateValue),
      time: timeForSeat(baseTime, seat || 1),
      seat: seat ? `Loc ${seat}` : "—",
      student: reservationStudentName(reservation) || "—",
      instructor: instructor || "—",
      sector: readFirst(
         reservation?.sector,
         raw.sector,
         rawSlot?.sector,
         slot?.sector,
         "—",
      ),
      gearbox: gearboxLabel(
         readFirst(
            reservation?.gearbox,
            raw.gearbox,
            rawSlot?.gearbox,
            slot?.gearbox,
            "",
         ),
      ),
      status:
         STATUS_LABELS[normalizeStatus(reservation?.status)] ||
         normalizeStatus(reservation?.status),
      studentPhone: readFirst(
         reservation?.studentPhone,
         reservation?.phone,
         pickStudentPhoneValue(reservation?.user),
         pickStudentPhoneValue(reservation?.student),
         raw.studentPhone,
         raw.phone,
         pickStudentPhoneValue(raw.user),
         pickStudentPhoneValue(raw.student),
         pickStudentPhoneValue(raw.reservation?.user),
         pickStudentPhoneValue(raw.reservation?.student),
      ),
      instructorPhone: readFirst(
         reservation?.instructorPhone,
         pickPersonPhoneValue(reservation?.instructor),
         pickPersonPhoneValue(reservation?.teacher),
         raw.instructorPhone,
         raw.teacherPhone,
         pickPersonPhoneValue(raw.instructor),
         pickPersonPhoneValue(raw.teacher),
         pickPersonPhoneValue(rawSlot?.instructor),
         pickPersonPhoneValue(rawSlot?.teacher),
         slot?.instructorPhone,
         slot?.teacherPhone,
         pickPersonPhoneValue(slot?.raw?.instructor),
         pickPersonPhoneValue(slot?.raw?.teacher),
      ),
   };
}

export default function PracticalExamBookStudentPopup({
   slot,
   seatNumber,
   reservation = null,
   gradeOnlyMode = false,
   onSaved,
   onAlert,
   onClose,
}) {
   const dispatch = useDispatch();
   const { list: students = [], loading } = useSelector(
      (state) => state.students || {},
   );
   const [studentId, setStudentId] = useState("");
   const [newFullName, setNewFullName] = useState("");
   const [phoneFull, setPhoneFull] = useState("");
   const [highlightedStudentId, setHighlightedStudentId] = useState(null);
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState("");
   const [colorToken, setColorToken] = useState(() =>
      normalizeReservationColor(reservation),
   );
   const [statusToken, setStatusToken] = useState(() =>
      normalizeEditableStatus(reservation?.status),
   );
   const [privateMessage, setPrivateMessage] = useState(() =>
      readReservationNote(reservation),
   );
   const [colorHoverText, setColorHoverText] = useState("");
   const [downloadingBorderou, setDownloadingBorderou] = useState(false);

   const reservationApiId = readReservationApiId(reservation);
   const isOccupied = Boolean(reservationApiId || reservation?.id);
   // Borderoul rezervării: documentul elevului, completat de backend din
   // datele examenului. Procesul-verbal al zilei se ia din antetul zilei.
   const canDownloadBorderou =
      EXAM_DOCUMENTS_ENABLED &&
      Boolean(reservationApiId) &&
      !gradeOnlyMode;
   const slotId = slot?.id;
   const savedColorToken = normalizeReservationColor(reservation);
   const selectedColorToken = normalizeColorToken(colorToken);
   const savedStatusToken = normalizeEditableStatus(reservation?.status);
   const savedPrivateMessage = readReservationNote(reservation);
   const selectedStatusToken = normalizeEditableStatus(statusToken);
   const colorChanged = selectedColorToken !== savedColorToken;
   const statusChanged =
      Boolean(selectedStatusToken) && selectedStatusToken !== savedStatusToken;
   const noteChanged = privateMessage !== savedPrivateMessage;
   const canSaveEditReservation =
      Boolean(reservationApiId) &&
      !saving &&
      (colorChanged || statusChanged || noteChanged);
   const reservationCard = buildReservationCardData(slot, reservation, seatNumber);
   const reservationDateTime = formatReservationDateTime(
      reservationCard.date,
      reservationCard.time,
   );
   const studentNameCopyValue =
      reservationCard.student && reservationCard.student !== "—"
         ? reservationCard.student
         : "";
   const studentPhoneCopyValue = String(
      reservationCard.studentPhone || "",
   ).trim();
   const reservationDetails = [
      {
         label: "Student",
         primary: reservationCard.student,
         secondary: reservationCard.studentPhone || "Telefon elev",
         copyPrimary: studentNameCopyValue,
         copySecondary: studentPhoneCopyValue,
         copyPrimaryLabel: "Nume elev",
         copySecondaryLabel: "Telefon elev",
      },
      {
         label: "Instructor",
         primary: reservationCard.instructor,
         secondary: reservationCard.instructorPhone || "Telefon instructor",
      },
      { label: "Data și ora", primary: reservationDateTime },
      { label: "Loc", primary: reservationCard.seat },
      ...(normalizeStatus(reservation?.status) !== "SCHEDULED"
         ? [{ label: "Status", primary: reservationCard.status }]
         : []),
      { label: "Sector", primary: reservationCard.sector },
      { label: "Cutie", primary: reservationCard.gearbox },
   ];
   const studentLookup = useMemo(() => buildStudentLookup(students), [students]);
   const selectedStudent = useMemo(() => {
      if (!studentId) return null;
      const sid = String(studentId);
      return (
         (Array.isArray(students) ? students : []).find(
            (student) =>
               hasUserRole(student) && String(student?.id ?? "") === sid,
         ) || null
      );
   }, [studentId, students]);
   const filteredStudents = useMemo(
      () =>
         findStudentsForSearch(
            studentLookup.searchIndex,
            { nameQuery: newFullName, phoneQuery: phoneFull },
            SEARCH_RESULTS_LIMIT * 2,
         ),
      [newFullName, phoneFull, studentLookup],
   );
   const studentsForSearchList = useMemo(() => {
      const base = Array.isArray(filteredStudents) ? filteredStudents : [];
      if (!highlightedStudentId) return base.slice(0, SEARCH_RESULTS_LIMIT);
      const idStr = String(highlightedStudentId);
      const index = base.findIndex((student) => String(student?.id) === idStr);
      if (index === -1) return base.slice(0, SEARCH_RESULTS_LIMIT);
      const copy = [...base];
      const [match] = copy.splice(index, 1);
      return [match, ...copy].slice(0, SEARCH_RESULTS_LIMIT);
   }, [filteredStudents, highlightedStudentId]);
   const hasStudentSearchQuery =
      onlyDigits(phoneFull).length > 0 ||
      normalizeNameForSearch(newFullName).length >= 2;
   const studentMatches = hasStudentSearchQuery
      ? studentsForSearchList.slice(0, SEARCH_RESULTS_LIMIT)
      : [];
   const selectedStudentPhone = selectedStudent
      ? pickStudentPhoneValue(selectedStudent)
      : "";
   const selectedStudentLabel = selectedStudent
      ? studentName(selectedStudent)
      : "Alege elev";
   const validNewName = newFullName.trim().split(/\s+/).length >= 2;
   const validNewPhone = onlyDigits(phoneFull).length > 0;
   const canSubmitWithStudentData =
      Boolean(studentId) || (validNewName && validNewPhone);

   const reportAlert = (type, message) => {
      const text = String(message || "A apărut o eroare.").trim();
      if (typeof onAlert === "function") {
         onAlert(type, text);
      } else if (type === "error" || type === "warning") {
         setError(text);
      }
   };

   const reportError = (message) => {
      reportAlert("error", message);
   };

   useEffect(() => {
      if (!students?.length) dispatch(fetchStudents());
   }, [dispatch, students?.length]);

   useEffect(() => {
      setColorToken(normalizeReservationColor(reservation));
      setStatusToken(normalizeEditableStatus(reservation?.status));
      setPrivateMessage(readReservationNote(reservation));
   }, [reservation]);

   useEffect(() => {
      if (isOccupied) return;

      const digits = onlyDigits(phoneFull);
      if (!digits) {
         setHighlightedStudentId(null);
         return;
      }

      const exact = findStudentByPhoneDigits(
         studentLookup.exactPhoneDigits,
         digits,
      );
      if (!exact) {
         setHighlightedStudentId(null);
         return;
      }

      const exactId = String(exact.id);
      setHighlightedStudentId(exactId);
      if (digits.length > 5) {
         setStudentId((prev) =>
            String(prev ?? "") === exactId ? prev : exactId,
         );
      }
   }, [isOccupied, phoneFull, studentLookup.exactPhoneDigits]);

   const waitUntilStudentInListByPhone = async (
      phoneDigits,
      tries = 12,
      delayMs = 250,
   ) => {
      const digits = onlyDigits(phoneDigits);
      if (!digits) return "";

      for (let index = 0; index < tries; index += 1) {
         let nextStudents = students;
         try {
            const action = await dispatch(fetchStudents());
            if (Array.isArray(action?.payload)) nextStudents = action.payload;
         } catch {}

         const lookup = buildStudentLookup(nextStudents);
         const found = findStudentByPhoneDigits(lookup.exactPhoneDigits, digits);
         if (found?.id) return String(found.id);

         await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      return "";
   };

   const ensureStudentForBooking = async () => {
      const currentStudentId = String(studentId || "").trim();
      if (currentStudentId) return currentStudentId;

      const { firstName, lastName } = splitFullName(newFullName);
      if (!firstName || !lastName) {
         reportError("Scrie numele complet al elevului.");
         return "";
      }

      const rawDigits = onlyDigits(phoneFull);
      if (!rawDigits) {
         reportError("Introdu un număr de telefon pentru elev.");
         return "";
      }

      const digits = normalizePhoneForStudentCreate(rawDigits);
      const existingSamePhone =
         findStudentByPhoneDigits(studentLookup.exactPhoneDigits, rawDigits) ||
         findStudentByPhoneDigits(studentLookup.exactPhoneDigits, digits);

      if (existingSamePhone?.id) {
         const existingId = String(existingSamePhone.id);
         setStudentId(existingId);
         setHighlightedStudentId(existingId);
         setNewFullName("");
         setPhoneFull("");
         reportAlert(
            "info",
            "Există deja un elev cu acest număr. A fost selectat automat.",
         );
         return existingId;
      }

      // cont de oaspete (doar nume + telefon): fără grupă, nu intră în
      // platformă și nu primește notificări, emailuri sau mementouri
      try {
         const response = await apiClientService.post("/users/guests", {
            firstName,
            lastName,
            phone: digits,
         });

         if (response.ok) {
            let created = {};
            try {
               created = await response.json();
            } catch {}

            let newId = String(created?.id ?? "") || "";
            if (!newId) newId = await waitUntilStudentInListByPhone(digits);

            if (!newId) {
               reportAlert(
                  "warning",
                  "Oaspete creat, dar nu am putut identifica ID-ul.",
               );
               return "";
            }

            try {
               await dispatch(fetchStudents());
            } catch {}

            setStudentId(newId);
            setHighlightedStudentId(newId);
            setNewFullName("");
            setPhoneFull("");
            reportAlert("success", "Oaspete nou adăugat (fără acces în platformă).");
            return newId;
         }

         let errorJson = null;
         try {
            errorJson = await response.json();
         } catch {}
         const message = Array.isArray(errorJson?.message)
            ? errorJson.message.join(" ")
            : errorJson?.message;
         reportError(message || "Nu am putut crea oaspetele.");
      } catch (err) {
         reportError(err?.message || "Eroare la crearea oaspetelui.");
      }

      const fallbackId = await waitUntilStudentInListByPhone(digits);
      if (fallbackId) {
         setStudentId(fallbackId);
         setHighlightedStudentId(fallbackId);
         setNewFullName("");
         setPhoneFull("");
         reportAlert("success", "Elev existent selectat automat după telefon.");
         return fallbackId;
      }

      reportError("Nu am putut crea elevul și nu l-am găsit după telefon.");
      return "";
   };

   const handlePhoneKeyDown = (event) => {
      if (event.key !== "Enter") return;

      event.preventDefault();
      if (saving) return;

      const digits = onlyDigits(phoneFull);
      if (!digits) return;

      const exact = findStudentByPhoneDigits(
         studentLookup.exactPhoneDigits,
         digits,
      );
      if (exact?.id) {
         const id = String(exact.id);
         setHighlightedStudentId(id);
         setStudentId(id);
         reportAlert("success", "Elev selectat automat după telefon.");
         return;
      }

      reportAlert(
         "warning",
         "Nu există potrivire exactă după telefon. Verifică numărul sau creează elevul.",
      );
   };

   const handleBook = async (event) => {
      event.preventDefault();
      if (!slotId || isOccupied) return;

      setSaving(true);
      setError("");

      try {
         const resolvedStudentId = await ensureStudentForBooking();
         if (!resolvedStudentId) return;

         const result = await bookPracticalExamSlotForStudent(
            slotId,
            {
               studentId: resolvedStudentId,
               color: resolvePracticalExamReservationColorForSave(colorToken),
               privateMessage: privateMessage.trim(),
            },
         );

         onSaved?.(result, "Student programat la examen practic.");
         onClose?.();
      } catch (err) {
         reportError(
            err?.message ||
               "Nu am putut programa studentul. Verifică slotul, studentul și drepturile admin/manager.",
         );
      } finally {
         setSaving(false);
      }
   };

   const handleCopyField = async (rawValue, label) => {
      const value = String(rawValue || "").trim();
      if (!value) return;

      const copied = await copyTextToClipboardSafe(value);
      reportAlert(
         copied ? "success" : "error",
         copied ? `${label} copiat.` : `Nu am putut copia ${label}.`,
      );
   };

   const handleSaveReservationEdit = async () => {
      if (!reservationApiId || saving) return;

      const nextColor = resolvePracticalExamReservationColorForSave(colorToken);
      const nextStatus = normalizeEditableStatus(statusToken);
      const shouldSaveColor = nextColor !== savedColorToken;
      const shouldSaveStatus = Boolean(nextStatus) && nextStatus !== savedStatusToken;
      const shouldSaveNote = privateMessage !== savedPrivateMessage;

      if (!shouldSaveColor && !shouldSaveStatus && !shouldSaveNote) {
         reportAlert("info", "Nu sunt modificări de salvat.");
         return;
      }

      setSaving(true);
      setError("");

      try {
         const result = await updatePracticalExamReservation(reservationApiId, {
            ...(shouldSaveColor ? { color: nextColor } : {}),
            ...(shouldSaveStatus ? { status: nextStatus } : {}),
            ...(shouldSaveNote
               ? { privateMessage: privateMessage.trim() }
               : {}),
         });
         if (shouldSaveColor) setColorToken(nextColor);
         if (shouldSaveStatus) setStatusToken(nextStatus);
         if (shouldSaveNote) {
            setPrivateMessage(String(result?.privateMessage ?? privateMessage));
         }

         onSaved?.(
            result,
            gradeOnlyMode ? "Calificativ salvat." : "Rezervare actualizată.",
         );
         onClose?.();
      } catch (err) {
         const message = String(err?.message || "");
         if (message.includes(" 404") || message.includes(" 405")) {
            reportError(
               "Backend-ul nu acceptă încă actualizarea acestei rezervări.",
            );
         } else {
            reportError(message || "Nu am putut salva modificările rezervării.");
         }
      } finally {
         setSaving(false);
      }
   };

   const handleDownloadBorderou = async () => {
      if (!reservationApiId || downloadingBorderou || saving) return;

      setDownloadingBorderou(true);
      setError("");
      try {
         await downloadPracticalExamIndividualDocument(reservationApiId);
         reportAlert("success", "Borderoul a fost descărcat.");
      } catch (err) {
         reportError(err?.message || "Nu am putut descărca borderoul.");
      } finally {
         setDownloadingBorderou(false);
      }
   };

   const handleDeleteReservation = async () => {
      if (!reservationApiId || saving) return;

      setSaving(true);
      setError("");

      try {
         const result = await deletePracticalExamReservation(reservationApiId);
         onSaved?.(result, "Rezervare ștearsă din slot.");
         onClose?.();
      } catch (err) {
         reportError(err?.message || "Nu am putut șterge rezervarea.");
      } finally {
         setSaving(false);
      }
   };

   const renderCreateField = ({
      label,
      primary,
      secondary = "",
      secondaryLabel = "",
      selected = false,
      full = false,
   }) => (
      <div
         className={[
            "createReservationPopup__field",
            full ? "" : "createReservationPopup__field--grow-1",
            selected ? "createReservationPopup__field--selected" : "",
         ]
            .filter(Boolean)
            .join(" ")}
      >
         <span className="createReservationPopup__field-label">{label}</span>
         <div className="createReservationPopup__field-line">
            <span className="createReservationPopup__field-text">
               {primary || "—"}
            </span>
         </div>
         {secondary ? (
            <>
               {secondaryLabel ? (
                  <span className="createReservationPopup__field-label">
                     {secondaryLabel}
                  </span>
               ) : null}
               <div className="createReservationPopup__field-line">
                  <span className="createReservationPopup__field-text">
                     {secondary}
                  </span>
               </div>
            </>
         ) : null}
      </div>
   );

   const colorGridStyle = useMemo(
      () => ({
         gridTemplateColumns: `repeat(${PRACTICAL_EXAM_COLOR_GRID_COLUMNS}, minmax(0, 1fr))`,
      }),
      [],
   );

   const renderColorSwatches = () =>
      PRACTICAL_EXAM_COLOR_TOKENS.map((token) => {
         const suffix = colorTokenSuffix(token);
         const active = selectedColorToken === normalizeColorToken(token);
         const label = PRACTICAL_EXAM_COLOR_LABELS[suffix] || suffix;
         const hint = PRACTICAL_EXAM_COLOR_HINTS[suffix] || label;

         return (
            <button
               key={token}
               type="button"
               role="radio"
               aria-checked={active}
               aria-label={hint}
               title={hint}
               className={[
                  "createReservationPopup__color-swatch",
                  `createReservationPopup__color-swatch--${suffix}`,
                  active ? "is-active" : "",
               ]
                  .filter(Boolean)
                  .join(" ")}
               style={colorSwatchStyle(token)}
               onClick={() => setColorToken(token)}
               onFocus={() => setColorHoverText(hint)}
               onBlur={() => setColorHoverText("")}
               onMouseEnter={() => setColorHoverText(hint)}
               onMouseLeave={() => setColorHoverText("")}
               disabled={saving}
            />
         );
      });

   const renderCreateColorControl = () => (
      <div className="createReservationPopup__field createReservationPopup__field--stacked createReservationPopup__field--grow-1 practical-exam-book-popup__color-create">
         <span className="createReservationPopup__field-label">Culoare</span>
         <div
            className="createReservationPopup__color-grid"
            role="radiogroup"
            aria-label="Culoare programare examen intern"
            style={colorGridStyle}
         >
            {renderColorSwatches()}
         </div>
         {colorHoverText ? (
            <div className="createReservationPopup__color-hint">
               {colorHoverText}
            </div>
         ) : null}
      </div>
   );

   const renderReservationDetail = ({
      label,
      primary,
      secondary = "",
      wide = false,
      copyPrimary = "",
      copySecondary = "",
      copyPrimaryLabel = "",
      copySecondaryLabel = "",
   }) => (
      <div
         key={label}
         className={[
            "instructorProfileUI__field",
            wide ? "instructorProfileUI__field--full" : "",
            copyPrimary || copySecondary
               ? "practical-exam-book-popup__copy-field"
               : "",
         ]
            .filter(Boolean)
            .join(" ")}
      >
         <span className="instructorProfileUI__fieldLabel">{label}</span>
         <div className="editReservationPopup__field-line practical-exam-book-popup__field-line">
            <span className="editReservationPopup__field-text practical-exam-book-popup__field-text">
               {primary || "—"}
            </span>
            {copyPrimary ? (
               <button
                  type="button"
                  className="editReservationPopup__copy-btn"
                  title={`Copiază ${copyPrimaryLabel.toLowerCase()}`}
                  aria-label={`Copiază ${copyPrimaryLabel.toLowerCase()}`}
                  onClick={() =>
                     void handleCopyField(copyPrimary, copyPrimaryLabel)
                  }
               >
                  <UIIcon
                     name="copy"
                     className="editReservationPopup__copy-icon"
                  />
               </button>
            ) : null}
         </div>
         {secondary ? (
            <div className="editReservationPopup__field-line practical-exam-book-popup__field-line">
               <span className="editReservationPopup__field-text practical-exam-book-popup__field-text">
                  {secondary}
               </span>
               {copySecondary ? (
                  <button
                     type="button"
                     className="editReservationPopup__copy-btn"
                     title={`Copiază ${copySecondaryLabel.toLowerCase()}`}
                     aria-label={`Copiază ${copySecondaryLabel.toLowerCase()}`}
                     onClick={() =>
                        void handleCopyField(copySecondary, copySecondaryLabel)
                     }
                  >
                     <UIIcon
                        name="copy"
                        className="editReservationPopup__copy-icon"
                     />
                  </button>
               ) : null}
            </div>
         ) : null}
      </div>
   );

   const renderEditOptionsControl = () => (
      <div className="instructorProfileUI__fieldGrid practical-exam-popup__field-grid practical-exam-book-popup__options-grid">
         <div className="instructorProfileUI__field instructorProfileUI__field--full instructorProfileUI__field--panel practical-exam-book-popup__edit-option practical-exam-book-popup__edit-option--status">
            <span className="instructorProfileUI__fieldLabel">
               {gradeOnlyMode ? "Calificativ" : "Status"}
            </span>
            <div
               className="instructorProfileUI__typeGrid practical-exam-book-popup__status-toggle"
               role="radiogroup"
               aria-label={
                  gradeOnlyMode
                     ? "Calificativ examen practic"
                     : "Status rezervare examen intern"
               }
            >
               {STATUS_OPTIONS.map((status) => {
                  const active = selectedStatusToken === status;
                  return (
                     <button
                        key={status}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={[
                           "instructorProfileUI__typeButton",
                           "practical-exam-book-popup__status-option",
                           active ? "is-active" : "",
                        ]
                           .filter(Boolean)
                           .join(" ")}
                        onClick={() => setStatusToken(status)}
                        disabled={saving}
                     >
                        {STATUS_LABELS[status] || status}
                     </button>
                  );
               })}
            </div>
         </div>

         <div className="instructorProfileUI__field instructorProfileUI__field--full instructorProfileUI__field--panel practical-exam-book-popup__edit-option practical-exam-book-popup__edit-option--color">
            <span className="instructorProfileUI__fieldLabel">Culoare</span>
            <div
               className="createReservationPopup__color-grid"
               role="radiogroup"
               aria-label="Culoare programare examen intern"
               style={colorGridStyle}
            >
               {renderColorSwatches()}
            </div>
            {colorHoverText ? (
               <div className="createReservationPopup__color-hint">
                  {colorHoverText}
               </div>
            ) : null}
         </div>

         <label className="instructorProfileUI__field instructorProfileUI__field--full instructorProfileUI__field--panel practical-exam-book-popup__edit-option">
            <span className="instructorProfileUI__fieldLabel">
               Notă rezervare
            </span>
            <textarea
               className="editReservationPopup__textarea"
               value={privateMessage}
               maxLength={191}
               onChange={(event) => setPrivateMessage(event.target.value)}
               placeholder="Notă pentru această rezervare de examen"
               disabled={saving}
            />
         </label>
      </div>
   );

   const createMode = !isOccupied;
   const rootClassName = createMode
      ? "createReservationPopup practical-exam-book-popup practical-exam-book-popup--create"
      : "instructorProfileUI practical-exam-popup practical-exam-book-popup practical-exam-book-popup--edit";
   const headerClassName = createMode
      ? "createReservationPopup__header"
      : "instructorProfileUI__header popupPanelUI__header";
   const titleClassName = createMode
      ? "createReservationPopup__title"
      : "instructorProfileUI__title";
   const contentClassName = createMode
      ? "createReservationPopup__content"
      : "instructorProfileUI__content practical-exam-popup__content";
   const title = createMode
      ? "Rezervare examen"
      : gradeOnlyMode
        ? "Calificativ examen"
        : "Editează rezervare";

   return (
      <div className={rootClassName}>
         <div className={headerClassName}>
            <h3 className={titleClassName}>{title}</h3>
         </div>

         <form className={contentClassName} onSubmit={handleBook}>
            {!isOccupied ? (
               <div className="createReservationPopup__group-body practical-exam-book-popup__student-search">
                  <div className="createReservationPopup__new-student">
                     <div className="createReservationPopup__form-row createReservationPopup__form-row--compact practical-exam-book-popup__student-fields">
                        <label className="createReservationPopup__field createReservationPopup__field--grow-1">
                           <span className="createReservationPopup__field-label">
                              Nume elev
                           </span>
                           <input
                              className="createReservationPopup__input"
                              placeholder="Nume Prenume"
                              value={newFullName}
                              onChange={(event) => {
                                 setNewFullName(event.target.value);
                                 if (event.target.value.trim()) setStudentId("");
                              }}
                              disabled={saving}
                           />
                        </label>

                        <label className="createReservationPopup__field createReservationPopup__field--grow-1">
                           <span className="createReservationPopup__field-label">
                              Telefon elev
                           </span>
                           <input
                              className="createReservationPopup__input"
                              placeholder="Telefon (număr)"
                              type="tel"
                              inputMode="numeric"
                              value={phoneFull}
                              onChange={(event) => {
                                 const nextDigits = onlyDigits(
                                    event.target.value,
                                 );
                                 setPhoneFull(nextDigits);
                                 if (nextDigits) setStudentId("");
                              }}
                              onKeyDown={handlePhoneKeyDown}
                              disabled={saving}
                           />
                        </label>
                     </div>
                  </div>

                  <ul className="createReservationPopup__search-list practical-exam-book-popup__student-list practical-exam-interne-search-list">
                     {!hasStudentSearchQuery ? (
                        <li className="createReservationPopup__search-empty">
                           Caută elevul după nume sau telefon.
                        </li>
                     ) : loading ? (
                        <li className="createReservationPopup__search-empty">
                           Se încarcă elevii...
                        </li>
                     ) : studentMatches.length === 0 ? (
                        <li className="createReservationPopup__search-empty">
                           Nu există potriviri.
                        </li>
                     ) : (
                        studentMatches.map((student) => {
                           const phone = pickStudentPhoneValue(student);
                           const isHighlighted =
                              highlightedStudentId &&
                              String(student?.id) === String(highlightedStudentId);
                           const isSingleStudentMatch =
                              studentMatches.length === 1;

                           return (
                              <li
                                 key={student.id}
                                 className={[
                                    "createReservationPopup__search-item",
                                    isHighlighted
                                       ? "createReservationPopup__search-item--highlighted"
                                       : "",
                                    isSingleStudentMatch
                                       ? "createReservationPopup__search-item--full"
                                       : "",
                                 ]
                                    .filter(Boolean)
                                    .join(" ")}
                                 onClick={() => {
                                    if (saving) return;
                                    setStudentId(String(student.id));
                                    setHighlightedStudentId(null);
                                    setPhoneFull("");
                                    setNewFullName("");
                                 }}
                              >
                                 <h3>
                                    {highlightText(
                                       studentName(student),
                                       newFullName,
                                    )}
                                 </h3>
                                 {phone ? (
                                    <p>{highlightText(phone, phoneFull)}</p>
                                 ) : null}
                              </li>
                           );
                        })
                     )}
                  </ul>
               </div>
            ) : null}

            {error ? (
               <p className="instructorProfileUI__error practical-exam-popup__error">
                  {error}
               </p>
            ) : null}

            {isOccupied ? (
               <div className="instructorProfileUI__fieldGrid practical-exam-popup__field-grid practical-exam-book-popup__field-grid">
                  {reservationDetails.map(renderReservationDetail)}
               </div>
            ) : (
               <div className="createReservationPopup__group-body practical-exam-book-popup__details-group">
                  <div className="createReservationPopup__form-row createReservationPopup__form-row--spaced">
                     {renderCreateField({
                        label: "Elev",
                        primary: selectedStudentLabel,
                        secondary: selectedStudentPhone || "Telefon elev",
                        selected: Boolean(selectedStudent),
                     })}
                     {renderCreateField({
                        label: "Instructor",
                        primary: reservationCard.instructor,
                        secondary:
                           reservationCard.instructorPhone || "Telefon instructor",
                     })}
                  </div>

                  <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
                     {renderCreateField({
                        label: "Data și ora",
                        primary: reservationDateTime,
                     })}
                     {renderCreateField({
                        label: "Loc",
                        primary: reservationCard.seat,
                     })}
                  </div>

                  <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
                     {renderCreateField({
                        label: "Sector",
                        primary: reservationCard.sector,
                     })}
                     {renderCreateField({
                        label: "Cutie",
                        primary: reservationCard.gearbox,
                     })}
                  </div>
                  {renderCreateColorControl()}
                  <label className="createReservationPopup__field createReservationPopup__field--stacked createReservationPopup__field--grow-1">
                     <span className="createReservationPopup__field-label">
                        Notă rezervare
                     </span>
                     <textarea
                        className="createReservationPopup__textarea"
                        value={privateMessage}
                        maxLength={191}
                        onChange={(event) =>
                           setPrivateMessage(event.target.value)
                        }
                        placeholder="Notă pentru această rezervare de examen"
                        disabled={saving}
                     />
                  </label>
               </div>
            )}

            {isOccupied ? (
               <div className="instructorProfileUI__section practical-exam-popup__section">
                  {renderEditOptionsControl()}

                  <div className="instructorProfileUI__actions practical-exam-popup__actions practical-exam-book-popup__actions">
                     <div className="createReservationPopup__actions-row practical-exam-book-popup__edit-actions">
                        {!gradeOnlyMode ? (
                           <ConfirmDeleteButton
                              className="practical-exam-popup__delete-confirm practical-exam-book-popup__delete-reservation"
                              onConfirm={handleDeleteReservation}
                              disabled={saving}
                              label="Șterge rezervarea"
                              confirmLabel={saving ? "Se procesează..." : "Da"}
                              cancelLabel="Nu"
                           />
                        ) : (
                           <span className="practical-exam-book-popup__edit-actions-spacer" aria-hidden="true" />
                        )}
                        <span
                           className="practical-exam-book-popup__edit-actions-spacer"
                           aria-hidden="true"
                        />
                        {canDownloadBorderou ? (
                           <button
                              type="button"
                              className="instructorProfileUI__button instructorProfileUI__button--edit practical-exam-book-popup__borderou-reservation"
                              onClick={handleDownloadBorderou}
                              disabled={saving || downloadingBorderou}
                              title="Descarcă borderoul acestei rezervări de examen"
                           >
                              {downloadingBorderou ? "Se descarcă..." : "Borderou"}
                           </button>
                        ) : null}
                        <button
                           type="button"
                           className="instructorProfileUI__button instructorProfileUI__button--save practical-exam-book-popup__save-reservation"
                           onClick={handleSaveReservationEdit}
                           disabled={!canSaveEditReservation}
                        >
                           {saving ? "Se salvează..." : "Salvează"}
                        </button>
                     </div>
                  </div>
               </div>
            ) : (
               <div className="createReservationPopup__group-body">
                  <div className="createReservationPopup__actions-row createReservationPopup__actions-row--two practical-exam-book-popup__create-actions">
                     <button
                        type="button"
                        className="createReservationPopup__btn createReservationPopup__btn--normal createReservationPopup__btn--action"
                        onClick={onClose}
                        disabled={saving}
                     >
                        Anulează
                     </button>
                     <button
                        type="submit"
                        className="createReservationPopup__btn createReservationPopup__btn--save createReservationPopup__btn--action"
                        disabled={saving || !canSubmitWithStudentData || !slotId}
                     >
                        {saving ? "Se programează..." : "Programează"}
                     </button>
                  </div>
               </div>
            )}
         </form>
      </div>
   );
}
